import {
  ArrowsRightLeftIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon,
} from '@heroicons/react/24/solid';

import { Badge } from '@/components/ui/badge';
import { DEFAULT_PER_SCAN_AI_BUDGET } from '@/hooks/usePerScanAIBudget';
import { cn } from '@/lib/utils';

import type { PipelineEstimate } from './types';

/**
 * One auto-inserted per-module llm override the cost panel should
 * caveat. Pure read-model: the editor computes these from the pipeline
 * doc (any ModuleRefObject whose `llm.provider` differs from the
 * top-level provider) and passes them in.
 */
export interface ModuleLlmOverrideAnnotation {
  module: string;
  provider: string;
  model: string;
}

interface CostPanelProps {
  estimate: PipelineEstimate;
  /**
   * When true, the metrics endpoint returned `auth-required` — the
   * platform's GitHub credential was rejected and the repo isn't
   * publicly accessible. The cost projection would be unreliable
   * (driven by placeholder size_bytes), so suppress the breakdown
   * and surface a loud "rotate the token" error instead.
   */
  metricsAuthRequired?: boolean;
  /**
   * When true, the metrics fetch resolved to pending — typically a
   * git-tree fetch failure (rate-limit, truncation, network). Hide
   * projection and show a "metrics pending" state. Never project on
   * a placeholder when the real size isn't known.
   */
  metricsPending?: boolean;
  /**
   * Per-module llm overrides the editor auto-inserted (or detected) on
   * ModuleRefObjects whose provider differs from the top-level. The
   * cost panel surfaces each as a small chip so users know cross-
   * provider routing is in effect — matches the constantine runner's
   * three-layer module-scoped override behavior.
   */
  moduleLlmOverrides?: ModuleLlmOverrideAnnotation[];
  /**
   * Per-tenant per-scan AI budget cap (USD), wired from usePerScanAIBudget.
   * Defaults to DEFAULT_PER_SCAN_AI_BUDGET when omitted.
   */
  maxBudgetUsd?: number;
}

const usd = (n: number) => `$${n < 10 ? n.toFixed(2) : Math.round(n)}`;

export function CostPanel({
  estimate,
  metricsAuthRequired,
  metricsPending,
  moduleLlmOverrides,
  maxBudgetUsd,
}: CostPanelProps) {
  const { total, stages, hasUnavailable } = estimate;

  const allUnavailable =
    stages.every(s => s.components.length === 0) && hasUnavailable;

  const budget = maxBudgetUsd ?? DEFAULT_PER_SCAN_AI_BUDGET;
  const budgetPct = (total.p50 / budget) * 100;
  const overBudget = total.p50 > budget;

  return (
    <aside
      aria-label="Pipeline cost estimate"
      className="border-chariot-stroke bg-chariot-background-primary sticky top-2 flex w-72 shrink-0 flex-col gap-4 self-start rounded-md border p-4"
    >
      <header className="flex items-center justify-between">
        <h4 className="text-chariot-text-primary text-xs font-semibold tracking-wider uppercase">
          Cost estimate
        </h4>
        {hasUnavailable && !allUnavailable && (
          <span
            className="text-chariot-status-warning"
            title="Some modules have no calibrated cost data"
          >
            <ExclamationTriangleIcon className="size-3.5" />
          </span>
        )}
      </header>

      {metricsAuthRequired ? (
        <AuthRequiredPanel />
      ) : metricsPending ? (
        <MetricsPendingPanel />
      ) : allUnavailable ? (
        <UnavailablePanel />
      ) : (
        <>
          <ProjectedBlock
            total={total}
            budget={budget}
            overBudget={overBudget}
          />
          <BudgetBar pct={budgetPct} overBudget={overBudget} />
          <BudgetEnforcementChip />

          <Separator />
          <StageBreakdown estimate={estimate} />
          <ModuleLlmOverrideChips overrides={moduleLlmOverrides} />
          <KnobBasisNote />
        </>
      )}

      {hasUnavailable && !allUnavailable && (
        <FootnoteUnavailable
          count={stages.reduce((acc, s) => acc + s.unavailable.length, 0)}
        />
      )}
    </aside>
  );
}

function ProjectedBlock({
  total,
  budget,
  overBudget,
}: {
  total: PipelineEstimate['total'];
  budget: number;
  overBudget: boolean;
}) {
  const sameLowHigh = total.low === total.high;
  return (
    <div className="flex flex-col gap-1">
      <span className="text-chariot-text-secondary text-[10px] font-medium tracking-wider uppercase">
        Projected
      </span>
      {sameLowHigh ? (
        <span
          className={cn(
            'text-3xl leading-none font-bold',
            overBudget
              ? 'text-chariot-status-critical'
              : 'text-chariot-text-primary'
          )}
        >
          {usd(total.p50)}
        </span>
      ) : (
        <div className="flex flex-col gap-0.5">
          <span
            className={cn(
              'text-2xl leading-none font-bold',
              overBudget
                ? 'text-chariot-status-critical'
                : 'text-chariot-text-primary'
            )}
          >
            {usd(total.low)} – {usd(total.high)}
          </span>
          <span className="text-chariot-text-secondary text-[11px]">
            p50 {usd(total.p50)}
          </span>
        </div>
      )}
      <span
        className="text-chariot-text-secondary mt-0.5 text-[11px]"
        title="Per-scan ceiling enforced by Guard for Constantine runs"
      >
        Per-scan cap{' '}
        <span className="text-chariot-text-primary font-medium">
          {usd(budget)}
        </span>
      </span>
    </div>
  );
}

function BudgetBar({ pct, overBudget }: { pct: number; overBudget: boolean }) {
  const clamped = Math.max(0, Math.min(100, pct));
  return (
    <div className="flex flex-col gap-1.5">
      <div className="bg-chariot-background-secondary relative h-2 w-full overflow-hidden rounded-full">
        <div
          className={cn(
            'h-full rounded-full transition-all',
            overBudget
              ? 'bg-chariot-status-critical'
              : 'bg-chariot-brand-primary'
          )}
          style={{ width: `${clamped}%` }}
        />
      </div>
      <span
        className={cn(
          'text-[11px]',
          overBudget
            ? 'text-chariot-status-critical font-medium'
            : 'text-chariot-text-secondary'
        )}
      >
        {clamped.toFixed(0)}% of budget at p50
        {overBudget && ' — over budget'}
      </span>
    </div>
  );
}

/**
 * Chip below the budget bar. The per-scan budget is always a HARD cap:
 * constantine is invoked with `--enforce-budget` unconditionally on every
 * run path (main scan, patch, validate, exploit), so its runtime halts the
 * scan once cumulative cost reaches the per-scan ceiling. The pipeline
 * doc's `enforce_budget` field is not wired to the flag and does not change
 * this, so the editor always presents the cap as binding.
 */
function BudgetEnforcementChip() {
  return (
    <div
      className="border-chariot-status-warning/40 bg-chariot-status-warning/5 text-chariot-status-warning flex items-start gap-1.5 rounded-sm border px-2 py-1 text-[10px] leading-snug"
      title="Constantine's runtime halts the scan once cumulative cost reaches the per-scan budget. On a large scan, expect partial findings."
    >
      <ExclamationTriangleIcon className="mt-0.5 size-3 shrink-0" />
      <span>
        <strong className="font-semibold">Hard cap.</strong> Scan halts when the
        per-scan budget is reached.
      </span>
    </div>
  );
}

function Separator() {
  return <div className="border-chariot-stroke border-t" />;
}

function StageBreakdown({ estimate }: { estimate: PipelineEstimate }) {
  return (
    <div className="flex flex-col gap-1.5">
      <span className="text-chariot-text-secondary text-[10px] font-medium tracking-wider uppercase">
        By stage
      </span>
      <div className="flex flex-col gap-1">
        {estimate.stages.map(stage => {
          const unavailable = stage.unavailable.length;
          const sumPriced = stage.p50;
          const isUnpriced = stage.components.length === 0 && unavailable > 0;
          return (
            <div
              key={stage.stage}
              className="flex items-baseline justify-between gap-2 py-0.5"
            >
              <span className="text-chariot-text-primary text-xs capitalize">
                {stage.stage}
              </span>
              <div className="flex items-baseline gap-1.5">
                <span
                  className={cn(
                    'text-xs font-medium',
                    isUnpriced
                      ? 'text-chariot-text-secondary'
                      : 'text-chariot-text-primary'
                  )}
                >
                  {isUnpriced ? '—' : usd(sumPriced)}
                </span>
                {unavailable > 0 && !isUnpriced && (
                  <span className="text-chariot-text-secondary text-[10px]">
                    +{unavailable} unpriced
                  </span>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/**
 * Per-module llm override chips. Rendered immediately under the by-stage
 * breakdown so the user sees which modules will route to a different
 * provider at runtime. Uses the existing `Badge` primitive to match the
 * incompatibility/calibration chip patterns elsewhere in the editor.
 *
 * Empty list → renders nothing. Each entry carries the chosen model so
 * users can verify the auto-inserted override matches their intent.
 */
function ModuleLlmOverrideChips({
  overrides,
}: {
  overrides: ModuleLlmOverrideAnnotation[] | undefined;
}) {
  if (!overrides || overrides.length === 0) return null;
  return (
    <div className="border-chariot-stroke flex flex-col gap-1.5 border-t pt-2.5">
      <span className="text-chariot-text-secondary text-[10px] font-medium tracking-wider uppercase">
        Provider overrides
      </span>
      <div className="flex flex-col gap-1">
        {overrides.map(o => (
          <Badge
            key={o.module}
            variant="outline"
            className="border-chariot-stroke text-chariot-text-secondary flex w-full items-start gap-1 px-1.5 py-1 text-[10px] leading-snug whitespace-normal"
            title={`Auto-inserted llm: block routes ${o.module}'s chat() calls through ${o.provider} for ${o.model}.`}
          >
            <ArrowsRightLeftIcon className="text-chariot-status-warning mt-0.5 size-3 shrink-0" />
            <span className="flex flex-col items-start gap-0.5">
              <span className="text-chariot-text-primary font-mono text-[10px]">
                {o.module}
              </span>
              <span>
                Provider override:{' '}
                <span className="text-chariot-text-primary font-semibold">
                  {o.provider}
                </span>{' '}
                <span className="font-mono">{o.model}</span>
              </span>
            </span>
          </Badge>
        ))}
      </div>
    </div>
  );
}

/**
 * Footer note below the stage breakdown. Reminds users that the
 * projection reflects the CURRENT knob config (max_findings,
 * layer1_chunk_size, etc.) — not what a past run on the same target
 * cost. A run that used a different `max_findings` cap or model
 * ensemble will appear cheaper or pricier than the editor shows.
 * Keeps the editor's contract honest: "what this config would cost,"
 * not "what some historical run cost."
 */
function KnobBasisNote() {
  return (
    <div className="border-chariot-stroke text-chariot-text-secondary flex items-start gap-1.5 border-t pt-2.5 text-[10px] leading-relaxed">
      <InformationCircleIcon className="mt-0.5 size-3 shrink-0" />
      <span>
        Projection based on current knob values. Historical runs that used
        different caps or model choices will not match exactly.
      </span>
    </div>
  );
}

function AuthRequiredPanel() {
  return (
    <div className="flex flex-col gap-2">
      <div className="bg-chariot-status-critical/10 text-chariot-status-critical flex size-9 items-center justify-center rounded-md">
        <ExclamationTriangleIcon className="size-5" />
      </div>
      <span className="text-chariot-text-primary text-sm font-semibold">
        Cost cannot be calculated
      </span>
      <p className="text-chariot-text-secondary text-xs leading-relaxed">
        The platform&apos;s GitHub credential was rejected and this repository
        isn&apos;t publicly accessible. Rotate the{' '}
        <code className="text-chariot-text-primary">GITHUB_TOKEN</code> in the{' '}
        <code className="text-chariot-text-primary">agent</code> SSM blob, then
        reload to retry. The scan can still be configured and run — only the
        projection is unavailable.
      </p>
    </div>
  );
}

function MetricsPendingPanel() {
  return (
    <div className="flex flex-col gap-2">
      <div className="bg-chariot-status-warning/10 text-chariot-status-warning flex size-9 items-center justify-center rounded-md">
        <InformationCircleIcon className="size-5" />
      </div>
      <span className="text-chariot-text-primary text-sm font-semibold">
        Repo metrics pending
      </span>
      <p className="text-chariot-text-secondary text-xs leading-relaxed">
        Couldn&apos;t walk the repository tree (GitHub rate limit, truncated
        listing, or transient network error). Cost cannot be projected without
        an accurate post-filter byte count. The scan can still be configured and
        run — retry the asset selection in a minute to refresh the projection.
      </p>
    </div>
  );
}

function UnavailablePanel() {
  return (
    <div className="flex flex-col gap-2">
      <div className="bg-chariot-status-warning/10 text-chariot-status-warning flex size-9 items-center justify-center rounded-md">
        <ExclamationTriangleIcon className="size-5" />
      </div>
      <span className="text-chariot-text-primary text-sm font-semibold">
        Estimate unavailable
      </span>
      <p className="text-chariot-text-secondary text-xs leading-relaxed">
        No modules have calibrated cost data yet. The pipeline will still run —
        projection appears once the constantine team publishes calibrated rates.
      </p>
    </div>
  );
}

function FootnoteUnavailable({ count }: { count: number }) {
  return (
    <div className="border-chariot-stroke text-chariot-text-secondary flex items-start gap-1.5 border-t pt-2.5 text-[10px] leading-relaxed">
      <InformationCircleIcon className="mt-0.5 size-3 shrink-0" />
      <span>
        {count} module{count === 1 ? '' : 's'} priced as unavailable — actual
        run will still execute, only projection is missing.
      </span>
    </div>
  );
}
